/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from 'react';

import moment from 'moment';
import 'mapbox-gl/dist/mapbox-gl.css';

import { Button, CheckboxInput, Input, Typography } from 'design_system/src';
import './recovery.scss';
import { useNavigate } from 'react-router-dom';
import {
	RepossessionEvidenceType,
	RepossessionStatus,
} from 'models/repossession/enums/repossession.enums';
import { RecoveryStatus } from 'models/repossession/enums/recovery.enums';
import { ReactComponent as Image } from 'design_system/src/static/icons/gallery.svg';
import { ReactComponent as Note } from 'design_system/src/static/icons/note.svg';

import { PopulatedRepossession } from 'models/repossession/interfaces/repossession.interface';
import {
	uploadFileRepossession,
	updateRepossession,
	updateRoute,
} from 'models/repossession/controllers/repossession.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import LoadRecoveryEvidence from './loadRecoveryEvidence/loadRecoveryEvidence';
import { repossessionMobileContext } from './RepossessionsMobileContext';

moment.locale('es');

export const RoutesFinalReportMobile = () => {
	const navigate = useNavigate();
	const { loading, route, recoveries, completedRecoveries, initializeContext } =
		useContext(repossessionMobileContext);

	const [repossessions, setRepossessions] = useState<
		{ repossession: PopulatedRepossession; status: RepossessionStatus }[]
	>([]);
	const [indexRepossession, setIndexRepossession] = useState(0);
	const [loadingRepo, setLoadingRepo] = useState(false);

	const [vehicleFront, setVehicleFront] = useState<File>();
	const [vehicleBack, setVehicleBack] = useState<File>();
	const [vehicleLeft, setVehicleLeft] = useState<File>();
	const [vehicleRight, setVehicleRight] = useState<File>();
	const [vehiclePanel, setVehiclePanel] = useState<File>();
	const [finalMileage, setFinalMileage] = useState(0);
	const [hasMileage, setHasMileage] = useState(true);

	const [nextAction, setNextAction] = useState();

	useEffect(() => {
		if (completedRecoveries !== recoveries.length) {
			navigate('/routes');
		}
	}, []);
	useEffect(() => {
		const finalRepossessions: {
			[key: string]: {
				repossession: PopulatedRepossession;
				status: RepossessionStatus;
			};
		} = {};
		recoveries.forEach((recovery) => {
			if (
				[RepossessionStatus.canceled, RepossessionStatus.completed].includes(
					finalRepossessions[recovery.repossession._id]?.status,
				)
			)
				return;

			let status = RepossessionStatus.pending;

			if (recovery.status === RecoveryStatus.recovered) {
				status = RepossessionStatus.completed;
			} else if (recovery.repossession.status === RepossessionStatus.canceled) {
				status = RepossessionStatus.canceled;
			}

			finalRepossessions[recovery.repossession._id] = {
				repossession: recovery.repossession,
				status,
			};
		});
		setRepossessions(Object.values(finalRepossessions));
	}, [recoveries, route]);
	useEffect(() => {
		setVehicleBack(undefined);
		setVehicleFront(undefined);
		setVehicleLeft(undefined);
		setVehiclePanel(undefined);
		setVehicleRight(undefined);
		setFinalMileage(0);
		setHasMileage(true);
		setNextAction(undefined);
		if (
			route &&
			repossessions.length &&
			indexRepossession >= repossessions.length
		) {
			updateRoute(route._id, { status: 'completed' }).then(() => {
				toasterNotification({
					msg: 'Ruta completada con exito',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				initializeContext();
				navigate('/routes');
			});
		}
	}, [indexRepossession]);

	const finishRepossession = () => {
		if (
			!vehicleFront ||
			!vehicleBack ||
			!vehicleLeft ||
			!vehicleRight ||
			!vehiclePanel
		)
			return;
		const files = [
			{ file: vehicleFront, name: RepossessionEvidenceType.vehicleFront },
			{ file: vehicleBack, name: RepossessionEvidenceType.vehicleBack },
			{ file: vehicleLeft, name: RepossessionEvidenceType.vehicleLeft },
			{ file: vehicleRight, name: RepossessionEvidenceType.vehicleRight },
			{ file: vehiclePanel, name: RepossessionEvidenceType.vehiclePanel },
		];
		const p = files.map((file) => {
			const blob = file.file.slice(0, file.file.size, file.file.type);
			const newFile = new File(
				[blob],
				`${repossessions[indexRepossession].repossession._id}_${file.name}`,
				{
					type: file.file.type,
				},
			);
			return uploadFileRepossession(
				repossessions[indexRepossession].repossession._id,
				newFile,
				file.name,
			);
		});
		setLoadingRepo(true);
		Promise.all(p)
			.then(() => {
				return updateRepossession(
					repossessions[indexRepossession].repossession._id,
					{
						status: RepossessionStatus.completed,
						finalMileage: hasMileage
							? `${finalMileage}`
							: 'No es posible determinar',
					},
				);
			})
			.then(() => {
				toasterNotification({
					msg: 'Recuperacion Finalizada con exito',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				setLoadingRepo(false);
				setIndexRepossession((prev) => prev + 1);
			})
			.catch((e) => {
				commonErrorHandler(e);
				setLoadingRepo(false);
			});
	};

	const processRepossession = () => {
		if (nextAction) {
			setLoadingRepo(true);
			updateRepossession(repossessions[indexRepossession].repossession._id, {
				nextActions: [nextAction],
			})
				.then(() => {
					toasterNotification({
						msg: 'Recuperacion Finalizada con exito',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					setIndexRepossession((prev) => prev + 1);
					setLoadingRepo(false);
				})
				.catch((e) => {
					setLoadingRepo(false);
					commonErrorHandler(e);
				});
		} else {
			toasterNotification({
				msg: 'Recuperacion Finalizada con exito',
				style: 'light',
				toasterType: toasterOptions.success,
			});
			setIndexRepossession((prev) => prev + 1);
			setLoadingRepo(false);
		}
	};

	return loading ||
		!route ||
		!repossessions.length ||
		indexRepossession >= repossessions.length ? (
		<Typography
			className="text_center w_100_per m_t_xxl"
			scale="large"
			weight="600"
		>
			Cargando información...
		</Typography>
	) : (
		<div>
			<Typography className="w_100_per m_b_md" scale="heading2" weight="600">
				{!indexRepossession ? '¡Hola de nuevo!' : '¡Continua así!'}
			</Typography>
			<div className="br_md shadow_hard display_flex flex_gap_md overflow_hidden bg_neutral_0">
				<div className="border_4 border_solid border_primary_300 recovery_final_photo_container br_md overflow_hidden flex_basis_0 flex_grow_1">
					<img
						src={
							repossessions[indexRepossession]?.repossession?.loan.vehicle
								.images?.[0]?.url ?? ''
						}
						alt=""
						className="img_cover"
					/>
				</div>
				<div className="p_md p_l_none flex_align_start flex_basis_0 flex_grow_2">
					<Typography scale="medium" weight="600">
						{
							repossessions[indexRepossession]?.repossession?.loan.vehicle
								.internalId
						}
					</Typography>
					<Typography scale="medium" weight="600">
						{repossessions[indexRepossession]?.repossession?.loan.vehicle?.brand
							?.name || '-'}{' '}
						{repossessions[
							indexRepossession
						]?.repossession?.loan.vehicle?.brand?.models?.find(
							(model) =>
								model._id ===
								(repossessions[indexRepossession]?.repossession?.loan.vehicle
									?.model as any),
						)?.name || '-'}{' '}
						{repossessions[indexRepossession]?.repossession?.loan.vehicle
							?.cylindersCapacity || '-'}{' '}
					</Typography>
					<Typography scale="medium" weight="400">
						{repossessions[indexRepossession]?.repossession?.loan.vehicle
							?.year || '-'}
					</Typography>
				</div>
			</div>
			{repossessions[indexRepossession]?.status ===
			RepossessionStatus.completed ? (
				<>
					<Typography scale="medium" weight="400" className="m_y_md">
						Sube fotos de la moto recuperada, asegurando que se vean los cuatro
						lados y el kilometraje.
					</Typography>
					<div className="flex_align_center flex_gap_xs display_flex">
						<CheckboxInput
							className="m_b_lg text_no_break"
							label="Se puede determinar el kilometraje?"
							name="isNewVehicle"
							value
							checked={hasMileage}
							onChange={(e: any) => setHasMileage(e.target.checked)}
						/>
					</div>
					<div className="display_flex flex_align_center flex_gap_xs">
						<Image className="dim_md text_primary_300" />
						<Typography scale="medium" weight="600">
							Kilometro final
						</Typography>
					</div>
					<Input
						title=""
						type="number"
						name="finalMileage"
						value={finalMileage}
						disabled={!hasMileage}
						onChange={(e: any) => setFinalMileage(e.target.value)}
						placeholder="ingresa el kilometraje de la moto"
					/>
					<div className="display_flex flex_align_center flex_gap_xs m_b_xs">
						<Image className="dim_md text_primary_300" />
						<Typography scale="medium" weight="600">
							Foto frontal
						</Typography>
					</div>
					<LoadRecoveryEvidence
						image={vehicleFront}
						setImage={setVehicleFront}
						className="m_b_md bg_neutral_0"
					/>
					<div className="display_flex flex_align_center flex_gap_xs m_b_xs">
						<Image className="dim_md text_primary_300" />
						<Typography scale="medium" weight="600">
							Foto trasera
						</Typography>
					</div>
					<LoadRecoveryEvidence
						image={vehicleBack}
						setImage={setVehicleBack}
						className="m_b_md bg_neutral_0"
					/>
					<div className="display_flex flex_align_center flex_gap_xs m_b_xs">
						<Image className="dim_md text_primary_300" />
						<Typography scale="medium" weight="600">
							Foto lateral izquierda
						</Typography>
					</div>
					<LoadRecoveryEvidence
						image={vehicleLeft}
						setImage={setVehicleLeft}
						className="m_b_md bg_neutral_0"
					/>
					<div className="display_flex flex_align_center flex_gap_xs m_b_xs">
						<Image className="dim_md text_primary_300" />
						<Typography scale="medium" weight="600">
							Foto lateral derecha
						</Typography>
					</div>
					<LoadRecoveryEvidence
						image={vehicleRight}
						setImage={setVehicleRight}
						className="m_b_md bg_neutral_0"
					/>
					<div className="display_flex flex_align_center flex_gap_xs m_b_xs">
						<Image className="dim_md text_primary_300" />
						<Typography scale="medium" weight="600">
							Foto panel de control
						</Typography>
					</div>
					<LoadRecoveryEvidence
						image={vehiclePanel}
						setImage={setVehiclePanel}
						className="m_b_md bg_neutral_0"
					/>
					<Button
						className="w_100_per m_t_md"
						scale="small"
						disabled={
							!vehicleFront ||
							!vehicleBack ||
							!vehicleLeft ||
							!vehicleRight ||
							!vehiclePanel ||
							(hasMileage && finalMileage < 1) ||
							loadingRepo
						}
						onClick={finishRepossession}
					>
						Finalizar Recuperacion
					</Button>
				</>
			) : (
				<>
					<Typography scale="medium" weight="400" className="m_y_md">
						{repossessions[indexRepossession]?.status !==
						RepossessionStatus.canceled
							? 'Escribe una nota sobre lo que se debe hacer la próxima vez para mejorar esta recuperación.'
							: 'La repossession fue cancelada, tiene algun comentario adicional'}
					</Typography>
					<Input
						type="textarea"
						name="comment"
						title={`Agregar nota${
							repossessions[indexRepossession]?.status ===
							RepossessionStatus.canceled
								? ' (opcional)'
								: ''
						}`}
						placeholder="Escribe una nota sobre el recovery"
						inputClassName="h_10_vp"
						icon={<Note />}
						value={nextAction}
						onChange={(e: any) => setNextAction(e.target.value)}
					/>
					<Button
						className="w_100_per m_t_md"
						scale="small"
						disabled={
							(repossessions[indexRepossession]?.status !==
								RepossessionStatus.canceled &&
								!nextAction) ||
							loadingRepo
						}
						onClick={processRepossession}
					>
						Registrar Información
					</Button>
				</>
			)}
		</div>
	);
};
